import React, {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {FormControl, Table} from "react-bootstrap";

import {ProgramFieldEditor} from "../../../Models/Core/FieldEditors/ProgramFieldEditor";
import {ProjectFieldEditor} from "../../../Models/Core/ServiceDrivenEditors";
import {service as ReportService} from "../../../Services/ReportService";

import {
    SET_MONITORING_EVENT_ID,
    SET_PHASE_ID,
    SET_PHASES,
    SET_PROCEDURE_ID,
    SET_PROCEDURES,
    SET_PROGRAM_ID,
    SET_PROJECT_ID,
    SET_REPORT_TYPE_ID,
    SET_REPORT_TYPES,
    SET_SECTIONS,
} from "../../../Redux/Actions/Types/reportActionTypes";
import {ReportSelectComponent} from "./ReportSelectComponent";


export const ParameterComponent = props => {
    const {reportTypes, phases, procedures, reportTypeId, programId, projectId, phaseId, procedureId, monitoringEventId} = useSelector(state => state.reportState);

    const dispatch = useDispatch();

    const reportCode = reportTypes?.find((type) => String(type.reportTypeId) === String(reportTypeId))?.code?.toLowerCase();
    
    const procedure = procedures?.find( p => String(p.procedureId) === String(procedureId) );
    
    let mostRecentRequestPromise = useRef(null);

    const showContext = (prefixes) => {
        if (!reportCode) {
            return false;
        }

        if(!(prefixes instanceof Array)) {
            prefixes = [prefixes];
        }

        return prefixes.some( prefix => reportCode.startsWith(prefix) );
    };

    const showPhase = (projectId || programId) && showContext('project', 'program'); // || sampleEventId;
    const showProcedure = projectId && showContext('monitoring-event');
    const showMonitoringEvent = projectId && procedureId && showContext('monitoring-event');
    
    // clear phaseId when phase is not applicable
    // to prevent filtering of monitoring events by phaseId
    // previous set from annual report parameters
    useEffect(() => {
        if (!showPhase && phaseId !== null) {
            dispatch({type: SET_PHASE_ID, phaseId: null});
        }
    }, [reportCode, showPhase, phaseId]);
    
    useEffect(() => {
        mostRecentRequestPromise.current = ReportService.Get(programId, projectId, phaseId).then((x) => {
            console.log(x.result);
            if(this !== mostRecentRequestPromise.current) {
                console.log('skipped');
            }

            // for now the only Monitoring Event Report we support is Photo Monitoring
            const supportedProcedures = (x.result.Procedures ?? []).filter( p => p.code === 'photo');
            
            dispatch({type: SET_REPORT_TYPES, reportTypes: x.result.ReportTypes ?? []});
            dispatch({type: SET_PHASES, phases: x.result.SampleEventPhases ?? []});
            dispatch({type: SET_SECTIONS, sections: x.result.ReportSections ?? []});
            dispatch({type: SET_PROCEDURES, procedures: supportedProcedures ?? []});
        });
    }, [projectId, phaseId]);

    return <div>
        <h3 className="table-headers">Main Parameters</h3>
        <Table striped bordered size="sm" className="sb-model-viewer">
            <tbody>
                <tr>
                    <td className="sb-model-viewer-column"><label>Report Type</label></td>
                    <td className="sb-model-viewer-column">
                        <FormControl
                            as="select"
                            value={reportTypeId}
                            onChange={(x) => dispatch({type: SET_REPORT_TYPE_ID, reportTypeId: x.target.value})}
                        >
                            <option value="">Select Report Type</option>
                            {
                                reportTypes?.map((type) => (
                                    <option key={type.reportTypeId} value={type.reportTypeId} disabled={type.disabled}>
                                        {type.name}
                                    </option>
                                ))
                            }
                        </FormControl>
                    </td>
                </tr>
                {
                    showContext('program') &&
                    <tr>
                        <td className="sb-model-viewer-column"><label>Program</label></td>
                        <td className="sb-model-viewer-column">
                            <ProgramFieldEditor
                                Value={programId}
                                OnUpdate={(value) => dispatch({type: SET_PROGRAM_ID, programId: value})}
                            />
                        </td>
                    </tr>
                }
                {
                    (showContext('project') || showContext('monitoring-event')) &&
                    <tr>
                        <td className="sb-model-viewer-column"><label>Project</label></td>
                        <td className="sb-model-viewer-column">
                            <ProjectFieldEditor
                                Value={projectId}
                                OnUpdate={(value) => dispatch({type: SET_PROJECT_ID, projectId: value})}
                                CustomData={{
                                    additionalSearchParams: {
                                        onlyWithMonitoringEvents: true,
                                    }
                                }}
                            />
                        </td>
                    </tr>
                }
                {
                    showPhase &&
                    <ReportSelectComponent
                        title="Phase"
                        primaryKey="sampleEventPhaseId"
                        data={phases}
                        value={phaseId}
                        onChange={(value) => dispatch({type: SET_PHASE_ID, phaseId: value})}
                        placeholder="Select Phase"
                        emptyMessage="This Project has no Monitoring Events."
                    />
                }
                {
                    !!showProcedure &&
                    <ReportSelectComponent
                        title="Procedure"
                        primaryKey="procedureId"
                        data={procedures}
                        value={procedureId}
                        onChange={(value) => dispatch({type: SET_PROCEDURE_ID, procedureId: value})}
                        placeholder="Select Procedure"
                        emptyMessage="No procedures available."
                    />
                }
                {
                    !!showMonitoringEvent &&
                    <ReportSelectComponent
                        title="Monitoring Event"
                        primaryKey="sampleEventId"
                        nameLogic={ (item) => item.projectPhaseName + ' - ' + item.shortName }
                        data={procedure?.SampleEvents ?? []}
                        value={monitoringEventId}
                        onChange={(value) => dispatch({type: SET_MONITORING_EVENT_ID, monitoringEventId: value})}
                        placeholder="Select Monitoring Event"
                        emptyMessage="No monitoring events available."
                    />
                }
            </tbody>
        </Table>
    </div>;
};


